<template>
  <ElCollapse
    v-model="openProductTypeIds"
    @change="toggleProductType"
  >
    <ElCollapseItem
      v-if="!isParentLevel"
      class="product-type-container"
      :name="productType.id"
    >
      <span
        slot="title"
        class="product-type-title-container"
        :style="{
          backgroundColor: TfProductSelector.themeHoverColor
            && hover
            ? TfProductSelector.themeHoverColor
            : null
        }"
        :data-test="`expand ${productType.name ? `${productType.name} ` : ''}products`"
        @mouseover="hover = true"
        @mouseout="hover = false"
      >
        <span class="product-type-title">
          {{ productType.name }}
          <slot
            name="productTypeAppend"
            :product-type="productType"
          />
        </span>
        <icon
          :class="[
            { 'is-active': isActive },
            'product-type-toggle'
          ]"
          :style="{
            fill: isActive && TfProductSelector.themePrimaryColor
              ? TfProductSelector.themePrimaryColor
              : null
          }"
          name="chevron-down"
        />
      </span>
      <ProductOption
        v-for="(productObj, index) in productType.products"
        :key="`product-${productObj.label}`"
        :product="productObj"
        :is-disabled="index ? productType.products[0].state !== 'complete' : false"
        :is-parent-level="false"
      >
        <!-- Used to control appended icons in component using <TfProductSelector /> -->
        <template
          slot="productOptionAppend"
          slot-scope="optionAppendProps"
        >
          <slot
            name="productOptionAppend"
            v-bind="optionAppendProps"
          />
        </template>
      </ProductOption>
    </ElCollapseItem>
    <ProductOption
      v-else
      :product="productType.products[0]"
      :is-parent-level="true"
    >
      <!-- Used to control appended icons in component using <TfProductSelector /> -->
      <template
        slot="productOptionAppend"
        slot-scope="optionAppendProps"
      >
        <slot
          name="productOptionAppend"
          v-bind="optionAppendProps"
        />
      </template>
    </ProductOption>
  </ElCollapse>
</template>

<script>
  import ProductOption from './ProductOption';

  /**
   * The "Product Types" for the Project Product Product selector.
   *
   * @vuedoc
   * @exports ProductSelector/TfProductType
   * @category Components
   */
  export default {
    name: 'TfProductType',
    components: { ProductOption },
    inject: [
      'TfProductSelector',
    ],
    provide() {
      return {
        productType: this,
      };
    },
    props: {
      productType: {
        type: Object,
        required: true,
        default: () => ({
          id: 0,
          name: null,
          products: [],
        }),
      },
      value: {
        type: Array,
        default: () => ([]),
      },
    },
    data: (vm) => ({
      hover: false,
      openProductTypeIds: vm.value,
    }),
    computed: {
      /**
       * Determines if the product type is expanded.
       *
       * @returns {boolean}
       */
      isActive() {
        return this.openProductTypeIds.includes(this.productType.id);
      },
      /**
       * Determine if the product should be in a child of a product type
       * based on the product label and product count.
       *
       * @returns {boolean}
       */
      isParentLevel() {
        const firstProduct = this.productType.products[0];

        return this.productType.products.length === 1
          && firstProduct.label === firstProduct.product_type_name;
      },
    },
    methods: {
      /**
       * Emits the updated model and emits "toggleProductType" from the TfProductSelector component.
       *
       */
      toggleProductType() {
        this.$emit('input', this.openProductTypeIds);
        this.TfProductSelector.$emit('toggleProductType', {
          action: this.openProductTypeIds.includes(this.productType.id)
            ? 'expand'
            : 'collapse',
          productTypeId: this.productType.id,
          openProductTypeIds: this.openProductTypeIds,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import 'src/assets/styles/variables';

  .product-type {
    &-title {
      display: flex;
      font-weight: 600;
    }

    &-title-container {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      padding: 8px 15px;
      font-size: 16px;
      border-bottom: 1px solid $tf-extra-light-gray-1;
      cursor: pointer;

      &:hover {
        background: $tf-light-blue;
      }
    }

    &-toggle {
      height: 18px;
      width: 16px;
      margin-top: 3px;
      fill: $tf-dark-gray;
      transition: transform .3s;
      transform: rotateZ(0);

      &.is-active {
        fill: $tf-blue;
        transform: rotateZ(180deg);
      }
    }
  }
</style>
